import { create } from 'zustand';
import {
  apiClient,
  AlertRuleResponse,
  AlertEventResponse,
  AlertSummaryResponse,
} from '@/utils/apiClient';

interface AlertState {
  rules: AlertRuleResponse[];
  events: AlertEventResponse[];
  summary: AlertSummaryResponse | null;
  loading: boolean;
  eventsLoading: boolean;
  error: string | null;
}

interface AlertActions {
  fetchRules: () => Promise<void>;
  createRule: (body: {
    name: string;
    rule_kind?: 'metric' | 'slo';
    metric_query: string;
    condition?: string;
    threshold?: number;
    severity?: string;
    notification_channels?: Record<string, any>;
    evaluation_interval?: number;
    description?: string;
    entity_selector?: string;
  }) => Promise<AlertRuleResponse>;
  updateRule: (id: number, body: {
    name?: string;
    rule_kind?: 'metric' | 'slo';
    metric_query?: string;
    condition?: string;
    threshold?: number;
    severity?: string;
    notification_channels?: Record<string, any>;
    evaluation_interval?: number;
    is_enabled?: boolean;
    description?: string;
    entity_selector?: string;
  }) => Promise<void>;
  deleteRule: (id: number) => Promise<void>;
  fetchEvents: (params?: {
    rule_id?: number;
    status?: string;
    severity?: string;
    since?: string;
    limit?: number;
  }) => Promise<void>;
  fetchSummary: () => Promise<void>;
  clearError: () => void;
}

interface AlertStore extends AlertState, AlertActions {}

export const useAlertStore = create<AlertStore>((set, get) => ({
  rules: [],
  events: [],
  summary: null,
  loading: false,
  eventsLoading: false,
  error: null,

  fetchRules: async () => {
    set({ loading: true, error: null });
    try {
      const rules = await apiClient.listAlertRules();
      set({ rules, loading: false });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load alert rules';
      set({ error: message, loading: false });
    }
  },

  // Write actions surface backend failures as a visible error and re-throw so the
  // caller (form modal) can keep itself open / show inline feedback, instead of
  // failing as an unhandled promise rejection (STORY-155 hardening).
  createRule: async (body) => {
    set({ error: null });
    try {
      const rule = await apiClient.createAlertRule(body);
      set({ rules: [rule, ...get().rules] });
      return rule;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create alert rule';
      set({ error: message });
      throw err;
    }
  },

  updateRule: async (id, body) => {
    set({ error: null });
    try {
      const updated = await apiClient.updateAlertRule(id, body);
      set({ rules: get().rules.map((r) => (r.id === id ? updated : r)) });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update alert rule';
      set({ error: message });
      throw err;
    }
  },

  deleteRule: async (id) => {
    set({ error: null });
    try {
      await apiClient.deleteAlertRule(id);
      set({ rules: get().rules.filter((r) => r.id !== id) });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete alert rule';
      set({ error: message });
      throw err;
    }
  },

  fetchEvents: async (params) => {
    set({ eventsLoading: true });
    try {
      const events = await apiClient.listAlertEvents(params);
      set({ events, eventsLoading: false });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load alert events';
      set({ error: message, eventsLoading: false });
    }
  },

  fetchSummary: async () => {
    try {
      const summary = await apiClient.getAlertSummary();
      set({ summary });
    } catch {
      // Non-critical
    }
  },

  clearError: () => set({ error: null }),
}));
